import { weeklyConsistency } from "../utils";

export function DayProgress({
  completionPercent,
  completedCount,
  totalCount,
  last7Dates,
  percentByDate,
  todayDate,
  streak,
}: {
  completionPercent: number;
  completedCount: number;
  totalCount: number;
  last7Dates: string[];
  percentByDate: Map<string, number>;
  todayDate: string;
  streak: number;
}) {
  const weekPercent = weeklyConsistency(last7Dates, percentByDate);

  return (
    <div className="p-4 rounded-2xl bg-muted/30 border border-border space-y-3">
      <div className="flex items-center gap-5">
        <CircularProgress
          value={completionPercent}
          completed={completedCount}
          total={totalCount}
        />
        <div className="flex-1 min-w-0">
          <SevenDayHeatmap
            dates={last7Dates}
            percentByDate={percentByDate}
            todayDate={todayDate}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground tabular-nums">
        {weekPercent}% this week
        {streak >= 2 && <span> · {streak}-day streak</span>}
      </p>
    </div>
  );
}

function CircularProgress({
  value,
  completed,
  total,
}: {
  value: number;
  completed: number;
  total: number;
}) {
  const size = 64;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{ position: "absolute", inset: 0 }}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-muted-foreground/15"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="stroke-primary transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-sm font-bold tabular-nums leading-none">{value}%</span>
        <span className="text-[9px] text-muted-foreground mt-0.5 tabular-nums">
          {completed}/{total}
        </span>
      </div>
    </div>
  );
}

function SevenDayHeatmap({
  dates,
  percentByDate,
  todayDate,
}: {
  dates: string[];
  percentByDate: Map<string, number>;
  todayDate: string;
}) {
  const dayLabels = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const maxBarHeight = 32;

  return (
    <div className="flex items-end justify-between gap-1">
      {dates.map((date) => {
        const pct = percentByDate.get(date) ?? 0;
        const isToday = date === todayDate;
        const dayOfWeek = new Date(`${date}T12:00:00Z`).getUTCDay();
        const label = dayLabels[dayOfWeek];
        const barHeight = Math.max(3, Math.round((pct / 100) * maxBarHeight));

        return (
          <div key={date} className="flex flex-col items-center gap-1 flex-1">
            <div className="w-full flex flex-col justify-end" style={{ height: maxBarHeight }}>
              <div
                title={`${pct}%`}
                style={{ height: barHeight }}
                className={`w-full rounded-sm transition-all duration-300 ${
                  pct === 0
                    ? "bg-muted-foreground/15"
                    : pct >= 80
                      ? "bg-primary"
                      : pct >= 40
                        ? "bg-primary/55"
                        : "bg-primary/25"
                }`}
              />
            </div>
            <span
              className={`text-[10px] leading-none tabular-nums ${
                isToday ? "text-primary font-bold" : "text-muted-foreground"
              }`}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
